import type { GroupedSearchStats } from './groupSearchStats';

export type SuggestionQueryConfig = {
  prefixes: ReadonlyArray<string>;
  suffixes: ReadonlyArray<string>;
};

export const DEFAULT_SUGGESTION_QUERY_CONFIG: SuggestionQueryConfig = {
  prefixes: [
    'All-inclusive hotels in',
    'Family holiday in',
    'Family friendly hotels in',
    'Family-friendly hotels in',
    'City escapes hotels in',
    'City escape hotels in',
    'Beachfront hotels in',
    'Holiday packages in',
  ],
  suffixes: [
    'hotels with breakfast included',
    'hotels with daily drink included',
    'hotels with airport transfer included',
    'hotels with free flow drinks included',
  ],
};

export type CategorisedSearchGroups = {
  suggestions: Array<GroupedSearchStats>;
  prefixMatched: Array<GroupedSearchStats>;
  other: Array<GroupedSearchStats>;
};

function sanitiseStringList(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      continue;
    }

    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

export function sanitiseSuggestionQueryConfig(input: unknown): SuggestionQueryConfig {
  if (!input || typeof input !== 'object') {
    return DEFAULT_SUGGESTION_QUERY_CONFIG;
  }

  const candidate = input as { prefixes?: unknown; suffixes?: unknown };

  if (!Array.isArray(candidate.prefixes) || !Array.isArray(candidate.suffixes)) {
    return DEFAULT_SUGGESTION_QUERY_CONFIG;
  }

  return {
    prefixes: sanitiseStringList(candidate.prefixes),
    suffixes: sanitiseStringList(candidate.suffixes),
  };
}

function mergeWithDefaults(defaults: ReadonlyArray<string>, values: ReadonlyArray<string>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of defaults) {
    const key = value.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(value);
    }
  }

  for (const value of values) {
    const key = value.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(value);
    }
  }

  return result;
}

export function mergeDefaultSuggestionQueryConfig(config: SuggestionQueryConfig): SuggestionQueryConfig {
  return {
    prefixes: mergeWithDefaults(DEFAULT_SUGGESTION_QUERY_CONFIG.prefixes, config.prefixes),
    suffixes: mergeWithDefaults(DEFAULT_SUGGESTION_QUERY_CONFIG.suffixes, config.suffixes),
  };
}

export function isSuggestionQuery(
  naturalLanguageQuery: string,
  config: SuggestionQueryConfig = DEFAULT_SUGGESTION_QUERY_CONFIG,
): boolean {
  const normalized = naturalLanguageQuery.trim().toLowerCase();

  return (
    config.prefixes.some((prefix) => normalized.startsWith(prefix.toLowerCase())) ||
    config.suffixes.some((suffix) => normalized.endsWith(suffix.toLowerCase()))
  );
}

function hasPrefixMatches(group: GroupedSearchStats): boolean {
  return group.prefixMatchedGeometryNames.length > 0 || group.prefixMatchedOfferNames.length > 0;
}

export function categoriseSearchGroups(
  groups: Array<GroupedSearchStats>,
  config: SuggestionQueryConfig = DEFAULT_SUGGESTION_QUERY_CONFIG,
): CategorisedSearchGroups {
  const suggestions: Array<GroupedSearchStats> = [];
  const prefixMatched: Array<GroupedSearchStats> = [];
  const other: Array<GroupedSearchStats> = [];

  for (const group of groups) {
    if (isSuggestionQuery(group.naturalLanguageQuery, config)) {
      suggestions.push(group);
    } else if (hasPrefixMatches(group)) {
      prefixMatched.push(group);
    } else {
      other.push(group);
    }
  }

  return { suggestions, prefixMatched, other };
}
